import { useState, useEffect, useMemo } from 'react';
import { clsx } from 'clsx';
import { Mail, Inbox, Send, RefreshCw, AlertCircle } from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from 'recharts';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { StatsCard } from '@/components/ui/StatsCard';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { Tabs } from '@/components/ui/Tabs';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { AiActionButton } from '@/components/ui/AiActionButton';
import { EmailClassificationBadge } from '@/components/modules/EmailClassificationBadge';
import { AutonomyIndicator } from '@/components/modules/AutonomyIndicator';
import { formatRelative } from '@/lib/format';
import { EmailSheet, type ComposePreFill } from '@/components/sheets/EmailSheet';
import { useEmailStore } from '@/stores/emailStore';
import { toast } from '@/stores/toastStore';
import { api } from '@/lib/api';
import type { ApiResponse } from '@nexus/shared/types';
import type { EmailMessage } from '@nexus/shared/types';

const CLASSIFICATION_COLORS: Record<string, string> = {
  support: '#f59e0b',
  sales: '#10b981',
  partnership: '#a855f7',
  invoice: '#3b82f6',
  legal: '#ef4444',
  spam: '#64748b',
  urgent: '#ef4444',
  other: '#94a3b8',
};

export function EmailOps() {
  const {
    messages,
    accounts,
    total,
    isLoading,
    error,
    fetchMessages,
    fetchAccounts,
    send,
    setSelected,
    clearError,
  } = useEmailStore();

  const [activeTab, setActiveTab] = useState('all');
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeForm, setComposeForm] = useState({
    accountId: '',
    to: '',
    cc: '',
    subject: '',
    body: '',
  });
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [aiDraftInstruction, setAiDraftInstruction] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchAccounts();
  }, [fetchMessages, fetchAccounts]);

  // Set default accountId once accounts load
  useEffect(() => {
    if (accounts.length > 0 && !composeForm.accountId) {
      setComposeForm((f) => ({ ...f, accountId: accounts[0].id }));
    }
  }, [accounts, composeForm.accountId]);

  const filtered = useMemo(() => {
    return messages.filter((e) => {
      if (activeTab === 'inbox') return e.direction === 'inbound';
      if (activeTab === 'sent') return e.direction === 'outbound';
      return true;
    });
  }, [messages, activeTab]);

  const emailTabs = useMemo(() => {
    const inbound = messages.filter((m) => m.direction === 'inbound').length;
    const outbound = messages.filter((m) => m.direction === 'outbound').length;
    return [
      { id: 'all', label: 'All', count: messages.length },
      { id: 'inbox', label: 'Inbox', count: inbound },
      { id: 'sent', label: 'Sent', count: outbound },
      { id: 'classified', label: 'Classified', count: messages.filter((m) => m.classification && (m.classification as string) !== 'unclassified').length },
    ];
  }, [messages]);

  const classificationDistribution = useMemo(() => {
    const counts: Record<string, number> = {};
    messages.forEach((m) => {
      const key = m.classification || 'other';
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: CLASSIFICATION_COLORS[name] || CLASSIFICATION_COLORS.other,
    }));
  }, [messages]);

  const unreadCount = useMemo(() => messages.filter((m) => !m.isRead).length, [messages]);

  const todayCount = useMemo(() => {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return messages.filter((m) => new Date(m.createdAt) >= todayStart).length;
  }, [messages]);

  const autoClassifiedPct = useMemo(() => {
    if (messages.length === 0) return '0%';
    const classified = messages.filter((m) => m.classification && (m.classification as string) !== 'unclassified').length;
    return `${((classified / messages.length) * 100).toFixed(1)}%`;
  }, [messages]);

  const handleSync = () => {
    fetchMessages();
  };

  const handleRowClick = (row: EmailMessage) => {
    setSelectedEmailId(row.id);
    setSelected(row.id);
  };

  const handleCloseSheet = () => {
    setSelectedEmailId(null);
    setSelected(null);
  };

  const handleComposeFromSheet = (preFill: ComposePreFill) => {
    setComposeForm((f) => ({
      ...f,
      to: preFill.to,
      subject: preFill.subject,
      body: preFill.body,
    }));
    setComposeOpen(true);
  };

  const handleCompose = async () => {
    if (!composeForm.to.trim() || !composeForm.subject.trim() || !composeForm.accountId) return;
    setComposing(true);
    try {
      await send({
        accountId: composeForm.accountId,
        to: [composeForm.to.trim()],
        cc: composeForm.cc.trim() ? [composeForm.cc.trim()] : undefined,
        subject: composeForm.subject,
        body: composeForm.body,
      });
      toast.success('Email sent');
      setComposeOpen(false);
      setComposeForm({ accountId: accounts[0]?.id || '', to: '', cc: '', subject: '', body: '' });
      fetchMessages();
    } catch {
      // error is set in store
    } finally {
      setComposing(false);
    }
  };

  const handleAiDraft = async () => {
    if (!aiDraftInstruction.trim()) return;
    setAiDrafting(true);
    try {
      const res = await api.post<ApiResponse<{ body: string }>>('/api/email/ai-draft', {
        instruction: aiDraftInstruction,
        context: composeForm.subject ? `Subject: ${composeForm.subject}` : undefined,
      });
      if (res.data?.body) {
        setComposeForm((f) => ({ ...f, body: res.data!.body }));
        setAiDraftOpen(false);
        setAiDraftInstruction('');
        toast.success('AI draft generated');
      }
    } catch {
      toast.error('AI Draft failed', 'Check that an AI provider is configured in Settings.');
    } finally {
      setAiDrafting(false);
    }
  };

  const columns: Column<EmailMessage>[] = [
    {
      key: 'status',
      header: '',
      className: 'w-6',
      render: (row) => (
        <div className={clsx('w-2 h-2 rounded-full', row.isRead ? 'bg-transparent' : 'bg-nexus-500')} />
      ),
    },
    {
      key: 'from',
      header: 'From / To',
      render: (row) => (
        <div>
          <p className={clsx('text-sm', row.isRead ? 'text-surface-300' : 'text-surface-100 font-medium')}>
            {row.direction === 'inbound' ? row.from : `To: ${row.to.join(', ')}`}
          </p>
        </div>
      ),
    },
    {
      key: 'subject',
      header: 'Subject',
      render: (row) => (
        <p className={clsx('truncate max-w-md', row.isRead ? 'text-surface-400' : 'text-surface-200 font-medium')}>
          {row.subject}
        </p>
      ),
    },
    {
      key: 'classification',
      header: 'Classification',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <EmailClassificationBadge classification={row.classification} />
          {row.autonomyAction && (
            <AutonomyIndicator level={2} action={row.autonomyAction} compact />
          )}
        </div>
      ),
    },
    {
      key: 'direction',
      header: 'Direction',
      render: (row) => (
        <Badge variant={row.direction === 'inbound' ? 'info' : 'success'}>
          {row.direction === 'inbound' ? 'In' : 'Out'}
        </Badge>
      ),
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400">{formatRelative(row.createdAt)}</span>,
    },
  ];

  if (isLoading && messages.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-28 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-20 rounded-lg" />
            <Skeleton className="h-9 w-28 rounded-lg" />
          </div>
        </div>
        {/* Stats skeleton — 4 cards */}
        <SkeletonStats count={4} />
        {/* Main grid: table 2/3 + sidebar 1/3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            {/* Tabs skeleton */}
            <div className="flex items-center gap-1 mb-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-20 rounded-lg" />
              ))}
            </div>
            {/* Email table — 6 columns */}
            <SkeletonTable rows={8} cols={6} />
          </div>
          <div className="space-y-6">
            {/* Classification chart skeleton */}
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
              <Skeleton className="h-4 w-48 mb-4" />
              <Skeleton className="h-52 w-full rounded-lg" />
            </div>
            {/* Connected accounts skeleton */}
            <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
              <Skeleton className="h-4 w-40 mb-4" />
              <div className="space-y-2">
                {Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Email Ops</h2>
          <p className="text-sm text-surface-400 mt-0.5">Unified email management with AI classification</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />} size="sm" onClick={handleSync} loading={isLoading}>
            Sync
          </Button>
          <Button icon={<Send className="h-4 w-4" />} size="sm" onClick={() => setComposeOpen(true)}>
            Compose
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="ml-auto text-xs text-red-400 hover:text-red-300">Dismiss</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="Emails Today" value={String(todayCount)} icon={<Mail className="h-5 w-5" />} />
        <StatsCard label="Unread" value={String(unreadCount)} icon={<Inbox className="h-5 w-5" />} />
        <StatsCard label="Auto-Classified" value={autoClassifiedPct} />
        <StatsCard label="Total Messages" value={String(total || messages.length)} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email table */}
        <div className="lg:col-span-2">
          {/* Tabs */}
          <div className="mb-4">
            <Tabs tabs={emailTabs} active={activeTab} onChange={setActiveTab} />
          </div>

          <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
            {filtered.length > 0 ? (
              <DataTable columns={columns} data={filtered} onRowClick={handleRowClick} />
            ) : (
              <EmptyState
                icon={<Mail className="h-8 w-8" />}
                title="No emails found"
                description="Messages will appear here once your accounts are synced."
              />
            )}
          </div>
        </div>

        {/* Right panel */}
        <div className="space-y-6">
          {/* Classification distribution */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">Classification Distribution</h3>
            {classificationDistribution.length > 0 ? (
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={classificationDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={75}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {classificationDistribution.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1e293b',
                        border: '1px solid #334155',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#f1f5f9',
                      }}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: '11px', color: '#94a3b8' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-surface-500 text-center py-8">No data yet</p>
            )}
          </div>

          {/* Connected accounts */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">Connected Accounts</h3>
            <div className="space-y-2">
              {accounts.length > 0 ? (
                accounts.map((acc) => (
                  <div
                    key={acc.id}
                    className="flex items-center justify-between px-3 py-2.5 bg-surface-900/50 rounded-lg border border-surface-700/50"
                  >
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-400" />
                      <div>
                        <p className="text-sm text-surface-200">{acc.email}</p>
                        <p className="text-xs text-surface-500">{acc.provider}</p>
                      </div>
                    </div>
                    <Badge variant={acc.isActive ? 'success' : 'default'}>
                      {acc.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-sm text-surface-500 text-center py-4">No accounts connected</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Email detail sheet */}
      <EmailSheet
        id={selectedEmailId}
        open={!!selectedEmailId}
        onClose={handleCloseSheet}
        onCompose={handleComposeFromSheet}
      />

      {/* Compose Modal */}
      <Modal open={composeOpen} onClose={() => setComposeOpen(false)} title="Compose Email">
        <div className="space-y-4">
          <Select
            label="From Account"
            options={accounts.map((a) => ({ value: a.id, label: a.email }))}
            value={composeForm.accountId}
            onChange={(e) => setComposeForm((f) => ({ ...f, accountId: e.target.value }))}
          />
          <Input
            label="To"
            type="email"
            placeholder="recipient@example.com"
            value={composeForm.to}
            onChange={(e) => setComposeForm((f) => ({ ...f, to: e.target.value }))}
          />
          <Input
            label="CC"
            type="email"
            placeholder="cc@example.com (optional)"
            value={composeForm.cc}
            onChange={(e) => setComposeForm((f) => ({ ...f, cc: e.target.value }))}
          />
          <Input
            label="Subject"
            placeholder="Email subject"
            value={composeForm.subject}
            onChange={(e) => setComposeForm((f) => ({ ...f, subject: e.target.value }))}
          />
          <div className="w-full">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-sm font-medium text-surface-300">Body</label>
              <AiActionButton
                size="sm"
                label={aiDraftOpen ? 'Close' : 'AI Draft'}
                onClick={() => setAiDraftOpen(!aiDraftOpen)}
                loading={false}
              />
            </div>
            {aiDraftOpen && (
              <div className="mb-2 p-3 bg-surface-900/80 border border-nexus-600/30 rounded-lg space-y-2">
                <p className="text-xs text-surface-400">Describe what you want to say...</p>
                <textarea
                  className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent min-h-[60px] resize-y"
                  placeholder="e.g. Follow up on our meeting last week about the partnership"
                  value={aiDraftInstruction}
                  onChange={(e) => setAiDraftInstruction(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-2.5 py-1 text-xs text-surface-400 hover:text-surface-200 transition-colors"
                    onClick={() => { setAiDraftOpen(false); setAiDraftInstruction(''); }}
                  >
                    Cancel
                  </button>
                  <AiActionButton
                    size="sm"
                    label="Generate"
                    onClick={handleAiDraft}
                    loading={aiDrafting}
                    disabled={!aiDraftInstruction.trim()}
                  />
                </div>
              </div>
            )}
            <textarea
              className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent min-h-[120px] resize-y"
              placeholder="Write your message..."
              value={composeForm.body}
              onChange={(e) => setComposeForm((f) => ({ ...f, body: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setComposeOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              icon={<Send className="h-4 w-4" />}
              onClick={handleCompose}
              loading={composing}
              disabled={!composeForm.to.trim() || !composeForm.subject.trim() || !composeForm.accountId}
            >
              Send
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
